/**
 * Empty state components for displaying "no content" messages
 */

import { ReactNode } from 'react'

interface EmptyStateProps {
  /** Main message to display */
  message: string
  /** Optional icon or emoji to show above the message */
  icon?: ReactNode | string
  /** Optional secondary message or description */
  description?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Empty state component
 * Shows a centered message when there's no content to display
 *
 * @example
 * ```tsx
 * <EmptyState
 *   message="No files generated yet"
 *   icon="📄"
 *   description="Files will appear here once generated"
 * />
 * ```
 */
export function EmptyState({ message, icon, description, className = '' }: EmptyStateProps) {
  return (
    <div className={`flex flex-col items-center justify-center w-full py-[24px] ${className}`}>
      {icon && (
        <div className='mb-[12px] text-[32px]'>{typeof icon === 'string' ? icon : icon}</div>
      )}
      <p className="font-['Geist'] font-medium text-[14px] text-[rgba(0,0,0,0.45)] text-center">
        {message}
      </p>
      {description && (
        <p className="font-['Geist'] font-normal text-[12px] text-[rgba(0,0,0,0.35)] text-center mt-[4px] max-w-[400px]">
          {description}
        </p>
      )}
    </div>
  )
}

interface EmptyStateWithActionProps {
  /** Main message to display */
  message: string
  /** Optional icon or emoji to show above the message */
  icon?: ReactNode | string
  /** Optional secondary message or description */
  description?: string
  /** Action button text */
  actionText?: string
  /** Action button click handler */
  onAction?: () => void
  /** Additional CSS classes */
  className?: string
}

/**
 * Empty state with action button
 * Shows a centered message with an optional action button
 */
export function EmptyStateWithAction({
  message,
  icon,
  description,
  actionText,
  onAction,
  className = ''
}: EmptyStateWithActionProps) {
  return (
    <div className={`flex flex-col items-center justify-center w-full py-[24px] ${className}`}>
      {icon && (
        <div className='mb-[12px] text-[32px]'>{typeof icon === 'string' ? icon : icon}</div>
      )}
      <p className="font-['Geist'] font-medium text-[16px] text-[rgba(0,0,0,0.88)] text-center mb-[8px]">
        {message}
      </p>
      {description && (
        <p className="font-['Geist'] font-normal text-[14px] text-[rgba(0,0,0,0.65)] text-center max-w-[400px]">
          {description}
        </p>
      )}
      {actionText && onAction && (
        <button
          onClick={onAction}
          className='mt-[16px] px-[16px] py-[8px] bg-[#9D7EE6] text-white rounded-[12px] font-medium text-[14px] hover:bg-[#8B6CD4] transition-colors'
        >
          {actionText}
        </button>
      )}
    </div>
  )
}

interface InlineEmptyStateProps {
  /** Message to display */
  message: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Minimal inline empty state
 * Used for smaller empty states within lists or sections
 */
export function InlineEmptyState({ message, className = '' }: InlineEmptyStateProps) {
  return (
    <div className={`flex items-center justify-center w-full py-[12px] ${className}`}>
      <span className="font-['Geist'] font-normal text-[12px] text-[rgba(0,0,0,0.45)]">
        {message}
      </span>
    </div>
  )
}
